import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Chip
} from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const ProfessionalTitleSelect = ({ selectedTitlesProp = [], onChange, error, helperText }) => {
  const [titles, setTitles] = useState([]);
  const [selectedTitles, setSelectedTitles] = useState(selectedTitlesProp);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setSelectedTitles(selectedTitlesProp);
  }, [selectedTitlesProp]);

  useEffect(() => {
    let active = true;
    const fetchTitles = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/professionalTitles", { 
          params: { pageNumber: 1, limit: 50, all: true } 
        });

        if (active) setTitles(data.titles || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchTitles();
    return () => { active = false; };
  }, []);

  const handleAddTitle = async (titleObj) => {
    const existing = titles.find(
      t => t.title.toLowerCase() === titleObj.title.toLowerCase()
    );

    if (existing) {
      if (!selectedTitles.some(t => t.id === existing.id)) {
        const newSelected = [...selectedTitles, existing];
        setSelectedTitles(newSelected);
        onChange(newSelected);
      }
      setInputValue("");
      toast.info("El título ya existía, se agregó a la selección");
      return;
    }

    try {
      setCreating(true);
      const { data } = await api.post("/professionalTitles", { title: titleObj.title });
      setTitles(prev => [data, ...prev]);

      const newSelected = [...selectedTitles, data];
      setSelectedTitles(newSelected);
      onChange(newSelected);

      toast.success("Título profesional creado correctamente");
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
      setInputValue("");
    }
  };
  
  const memoizedOptions = React.useMemo(() => {
    const optionsMap = new Map();
    selectedTitles.forEach(title => optionsMap.set(title.id, title));
    titles.forEach(title => {
      if (!optionsMap.has(title.id)) {
        optionsMap.set(title.id, title);
      }
    });
    return Array.from(optionsMap.values());
  }, [titles, selectedTitles]);

  return (
    <Box mb={2}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Buscar y seleccionar títulos del doctor. Puede crear uno nuevo escribiendo y presionando '+'.
      </Typography>

      <Autocomplete
        multiple
        value={selectedTitles}
        onChange={(e, newValues) => {
          setSelectedTitles(newValues);
          onChange(newValues);
        }}
        inputValue={inputValue}
        noOptionsText="No hay opciones"
        loadingText="Cargando..."
        onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
        options={memoizedOptions}
        getOptionLabel={(option) => option.title || ""}
        getOptionSelected={(option, value) => option.id === value.id}
        loading={loading}
        disableCloseOnSelect
        renderOption={(option, { selected }) => (
          <Typography>{option.title}</Typography>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              label={option.title}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label="Títulos profesionales"
            margin="dense"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(loading || creating) && <CircularProgress size={20} />}
                  <InputAdornment position="end">
                    {inputValue.trim() &&
                      !selectedTitles.some(t => t.title.toLowerCase() === inputValue.toLowerCase()) && (
                        <IconButton
                          size="small"
                          onClick={() => handleAddTitle({ title: inputValue.trim() })}
                          disabled={creating}
                        >
                          <Add />
                        </IconButton>
                      )}
                  </InputAdornment>
                </>
              )
            }}
          />
        )}
      />
      {error && (
        <Typography color="error" variant="caption">
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

ProfessionalTitleSelect.propTypes = {
  selectedTitlesProp: PropTypes.array,
  onChange: PropTypes.func.isRequired
};

export default ProfessionalTitleSelect;